/**
 * Admin — feature flags (bloc 2).
 *
 * Interrupteurs runtime on/off (table feature_toggles), modifiables sans
 * redéploiement. Distincts des entitlements par tier. Chaque bascule est
 * auditée côté serveur.
 */
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { listFeatureFlags, setFeatureFlag, type FeatureToggle } from "@/api/admin";

export default function AdminFlagsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "feature-flags"],
    queryFn: listFeatureFlags,
  });

  const toggleMut = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      setFeatureFlag(key, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "feature-flags"] }),
  });

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/admin" className="text-sm text-blue-900 underline">
          ← Admin
        </Link>
        <h1 className="mt-3 text-2xl font-bold">Feature flags</h1>
        <p className="mt-1 text-sm text-gray-600">
          Interrupteurs runtime, modifiables sans redéploiement.
        </p>

        {isLoading && <p className="mt-6 text-sm text-gray-500">Chargement…</p>}
        {error && (
          <p className="mt-6 text-sm text-red-700">Impossible de charger les flags.</p>
        )}

        {data && (
          <div className="mt-6 space-y-2">
            {data.length === 0 && (
              <p className="text-sm text-gray-500">Aucun flag défini.</p>
            )}
            {data.map((f) => (
              <FlagRow
                key={f.key}
                flag={f}
                busy={toggleMut.isPending}
                onToggle={(enabled) => toggleMut.mutate({ key: f.key, enabled })}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function FlagRow({
  flag,
  busy,
  onToggle,
}: {
  flag: FeatureToggle;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
      <div>
        <div className="font-mono text-sm font-medium">{flag.key}</div>
        {flag.description && (
          <div className="mt-0.5 text-xs text-gray-500">{flag.description}</div>
        )}
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => onToggle(!flag.enabled)}
        aria-pressed={flag.enabled}
        className={`rounded-full px-3 py-1 text-sm font-semibold disabled:opacity-50 ${
          flag.enabled
            ? "bg-green-100 text-green-800"
            : "bg-gray-200 text-gray-600"
        }`}
      >
        {flag.enabled ? "Activé" : "Désactivé"}
      </button>
    </div>
  );
}
